import type { ErrorCode, FileMeta, TransferMessage, TransferStatus } from '@flowshare/protocol';
import {
  MAX_FILE_BYTES,
  TRANSFER_CHUNK_SIZE,
  createTransferMessage,
  encodeChunkFrame,
} from '@flowshare/protocol';
import { createTransferId, sanitizeFileName } from '@flowshare/shared';

import type { TransferRecord } from '../../stores/transfer-store';
import { Sha256 } from '../crypto/sha256';
import {
  clampChunkSize,
  fileMetaFromFile,
  totalChunksFor,
  validateSelection,
  type TransferSettings,
  type TransferTransport,
} from '../webrtc/transport';
import { createReceiveWriter, type ReceiveWriter } from './storage';

export interface TransferSink {
  upsert: (record: TransferRecord) => void;
  patch: (id: string, patch: Partial<TransferRecord>) => void;
  remove: (id: string) => void;
  finish: (record: TransferRecord) => void;
  notify: (message: string, kind: 'success' | 'error' | 'info') => void;
}

export interface TransferManagerOptions {
  sink: TransferSink;
  getSettings: () => TransferSettings;
  onIncomingOffer?: (record: TransferRecord) => void;
  onAutoDownload?: (record: TransferRecord) => void;
  now?: () => number;
}

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  let reject!: (error: unknown) => void;
  const promise = new Promise<T>((resolvePromise, rejectPromise) => {
    resolve = resolvePromise;
    reject = rejectPromise;
  });
  // Avoid unhandled rejections when a transfer is cancelled before anyone awaits.
  promise.catch(() => undefined);
  return { promise, resolve, reject };
}

function createTimeout(milliseconds: number): { promise: Promise<never>; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const promise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('TIMEOUT')), milliseconds);
  });
  promise.catch(() => undefined);
  return {
    promise,
    cancel: () => {
      if (timer) clearTimeout(timer);
    },
  };
}

class CancelledByUser extends Error {
  constructor() {
    super('TRANSFER_CANCELLED');
    this.name = 'CancelledByUser';
  }
}

class TransferAbort extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly notifyPeer = true,
  ) {
    super(message);
    this.name = 'TransferAbort';
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof TransferAbort) return error.message;
  if (error instanceof Error) {
    if (error.message === 'TIMEOUT') return 'The other device stopped responding.';
    if (error.message === 'CHANNEL_CLOSED') return 'The peer connection closed.';
    return error.message;
  }
  return 'The transfer failed.';
}

interface SenderState {
  id: string;
  file: File;
  peerId: string;
  peerName: string;
  chunkSize: number;
  status: TransferStatus;
  record: TransferRecord;
  digest: Sha256;
  bytesSent: number;
  chunksSent: number;
  speed: number;
  sampleAt: number;
  sampleBytes: number;
  lastReportAt: number;
  cancelled: boolean;
  finalized: boolean;
  accept: Deferred<number>;
  done: Deferred<void>;
}

interface ReceiverState {
  id: string;
  peerId: string;
  peerName: string;
  file: FileMeta;
  chunkSize: number;
  writer: ReceiveWriter;
  digest: Sha256;
  bytesReceived: number;
  expectedIndex: number;
  writeChain: Promise<void>;
  writeError: unknown;
  record: TransferRecord;
  speed: number;
  sampleAt: number;
  sampleBytes: number;
  lastReportAt: number;
  lastActivityAt: number;
  completed: boolean;
  finalized: boolean;
}

interface PendingOffer {
  id: string;
  peerId: string;
  peerName: string;
  file: FileMeta;
  chunkSize: number;
  record: TransferRecord;
  transport: TransferTransport;
}

interface RetryEntry {
  file: File;
  peerId: string;
  peerName: string;
}

const IDLE_RECEIVE_TIMEOUT_MS = 90_000;
const ACCEPT_TIMEOUT_MS = 120_000;
const COMPLETE_TIMEOUT_MS = 120_000;

/**
 * Drives real, chunked file transfers over WebRTC data channels.
 *
 * - One control channel carries JSON, one bulk channel carries binary frames.
 * - Only one chunk is ever held in memory per active transfer.
 * - The sender pauses when the channel buffer passes the high-water mark.
 * - SHA-256 is computed incrementally on both sides and compared on completion.
 */
export class TransferManager {
  private readonly transports = new Map<string, TransferTransport>();
  private readonly senders = new Map<string, SenderState>();
  private readonly receivers = new Map<string, ReceiverState>();
  private readonly pendingOffers = new Map<string, PendingOffer>();
  private readonly pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly retryEntries = new Map<string, RetryEntry>();
  private readonly queue: string[] = [];
  private activeSenders = 0;
  private disposed = false;
  private readonly watchdog: ReturnType<typeof setInterval>;

  constructor(private readonly options: TransferManagerOptions) {
    this.watchdog = setInterval(() => this.sweepIdleReceivers(), 5_000);
  }

  private now(): number {
    return this.options.now?.() ?? Date.now();
  }

  /* ---------------------------------------------------------------------- */
  /* Peer lifecycle                                                         */
  /* ---------------------------------------------------------------------- */

  attachTransport(transport: TransferTransport): void {
    if (this.disposed) return;
    this.transports.set(transport.peerId, transport);
    this.pump();
  }

  detachAllTransports(reason = 'The room connection was reset.'): void {
    for (const peerId of [...this.transports.keys()]) {
      this.detachTransport(peerId, reason);
    }
  }

  detachTransport(peerId: string, reason = 'The peer connection was lost.'): void {
    this.transports.delete(peerId);

    for (const sender of [...this.senders.values()]) {
      if (sender.peerId === peerId && !sender.finalized) {
        this.failSender(sender, 'TRANSFER_FAILED', reason, false);
      }
    }
    for (const receiver of [...this.receivers.values()]) {
      if (receiver.peerId === peerId && !receiver.finalized) {
        void this.failReceiver(receiver, 'TRANSFER_FAILED', reason, false);
      }
    }
  }

  destroy(): void {
    this.disposed = true;
    clearInterval(this.watchdog);

    for (const sender of [...this.senders.values()]) {
      if (!sender.finalized) this.failSender(sender, 'TRANSFER_CANCELLED', 'Session ended.', false);
    }
    for (const receiver of [...this.receivers.values()]) {
      if (!receiver.finalized) {
        void receiver.writer.abort().catch(() => undefined);
        receiver.finalized = true;
      }
    }

    this.senders.clear();
    this.receivers.clear();
    this.pendingOffers.clear();
    for (const timer of this.pendingTimers.values()) clearTimeout(timer);
    this.pendingTimers.clear();
    this.transports.clear();
    this.queue.length = 0;
  }

  /* ---------------------------------------------------------------------- */
  /* Sending                                                                */
  /* ---------------------------------------------------------------------- */

  enqueue(files: File[], peers: Array<{ id: string; name: string }>): number {
    if (this.disposed) return 0;
    const problem = validateSelection(files);
    if (problem) {
      this.options.sink.notify(problem, 'error');
      return 0;
    }
    if (peers.length === 0) {
      this.options.sink.notify('Connect a device before sending files.', 'error');
      return 0;
    }

    let created = 0;
    for (const peer of peers) {
      for (const file of files) {
        const id = createTransferId();
        const now = this.now();
        const chunkSize = TRANSFER_CHUNK_SIZE;
        const record = this.makeRecord({
          id,
          direction: 'send',
          peerId: peer.id,
          peerName: peer.name,
          file: fileMetaFromFile(file),
          chunkSize,
          status: 'waiting',
          requiresAcceptance: false,
          canRetry: true,
          now,
        });

        this.senders.set(id, {
          id,
          file,
          peerId: peer.id,
          peerName: peer.name,
          chunkSize,
          status: 'waiting',
          record,
          digest: new Sha256(),
          bytesSent: 0,
          chunksSent: 0,
          speed: 0,
          sampleAt: now,
          sampleBytes: 0,
          lastReportAt: 0,
          cancelled: false,
          finalized: false,
          accept: createDeferred<number>(),
          done: createDeferred<void>(),
        });
        this.retryEntries.set(id, { file, peerId: peer.id, peerName: peer.name });
        this.queue.push(id);
        this.options.sink.upsert(record);
        created += 1;
      }
    }

    this.pump();
    return created;
  }

  private pump(): void {
    if (this.disposed) return;
    const maxConcurrent = Math.max(1, this.options.getSettings().maxConcurrentTransfers);

    while (this.activeSenders < maxConcurrent) {
      const nextId = this.queue.find((id) => {
        const sender = this.senders.get(id);
        if (!sender || sender.finalized || sender.status !== 'waiting') return false;
        return this.transports.get(sender.peerId)?.isOpen() === true;
      });

      if (!nextId) break;

      const index = this.queue.indexOf(nextId);
      if (index >= 0) this.queue.splice(index, 1);

      this.activeSenders += 1;
      void this.runSender(nextId)
        .catch(() => undefined)
        .finally(() => {
          this.activeSenders = Math.max(0, this.activeSenders - 1);
          this.pump();
        });
    }
  }

  private async runSender(id: string): Promise<void> {
    const sender = this.senders.get(id);
    if (!sender || sender.finalized) return;

    const transport = this.transports.get(sender.peerId);
    if (!transport || !transport.isOpen()) {
      this.failSender(sender, 'TRANSFER_FAILED', 'The peer connection is not ready.', false);
      return;
    }

    sender.status = 'connecting';
    this.patchSender(sender, { status: 'connecting' });

    const offer = createTransferMessage('transfer_offer', {
      transferId: sender.id,
      file: fileMetaFromFile(sender.file),
      chunkSize: sender.chunkSize,
    });

    if (!transport.sendControl(offer)) {
      this.failSender(sender, 'TRANSFER_FAILED', 'The peer connection closed.', false);
      return;
    }

    const acceptTimeout = createTimeout(ACCEPT_TIMEOUT_MS);
    try {
      const accepted = await Promise.race([sender.accept.promise, acceptTimeout.promise]);
      acceptTimeout.cancel();

      if (sender.finalized || sender.cancelled) return;

      sender.chunkSize = clampChunkSize(accepted || sender.chunkSize);
      sender.status = 'transferring';
      this.patchSender(sender, {
        status: 'transferring',
        chunkSize: sender.chunkSize,
        totalChunks: totalChunksFor(sender.file.size, sender.chunkSize),
        startedAt: this.now(),
      });

      await this.streamSender(sender, transport);
    } catch (error) {
      acceptTimeout.cancel();
      if (error instanceof CancelledByUser) return;
      const abort = error instanceof TransferAbort ? error : null;
      this.failSender(
        sender,
        abort?.code ?? 'TRANSFER_FAILED',
        errorMessage(error),
        abort?.notifyPeer ?? true,
      );
    }
  }

  private async streamSender(sender: SenderState, transport: TransferTransport): Promise<void> {
    const file = sender.file;
    let offset = 0;
    let index = 0;

    while (offset < file.size) {
      if (sender.finalized || sender.cancelled) throw new CancelledByUser();
      if (!transport.isOpen()) {
        throw new TransferAbort('TRANSFER_FAILED', 'The peer connection was lost.');
      }

      await transport.waitForDrain();
      if (sender.finalized || sender.cancelled) throw new CancelledByUser();

      const end = Math.min(offset + sender.chunkSize, file.size);
      const buffer = await file.slice(offset, end).arrayBuffer();
      const bytes = new Uint8Array(buffer);
      if (bytes.byteLength === 0) break;

      sender.digest.update(bytes);

      if (!transport.sendChunk(encodeChunkFrame(sender.id, index, bytes))) {
        throw new TransferAbort('TRANSFER_FAILED', 'The data channel closed.');
      }

      offset = end;
      index += 1;
      sender.bytesSent = offset;
      sender.chunksSent = index;
      this.reportSenderProgress(sender);
    }

    const digest = sender.digest.hex();
    sender.bytesSent = file.size;
    this.reportSenderProgress(sender, true);

    const completion = createTransferMessage('file_complete', {
      transferId: sender.id,
      sha256: digest,
      size: file.size,
      chunks: index,
    });

    if (!transport.sendDataMessage(completion)) {
      throw new TransferAbort('TRANSFER_FAILED', 'The data channel closed.');
    }

    const doneTimeout = createTimeout(COMPLETE_TIMEOUT_MS);
    try {
      await Promise.race([sender.done.promise, doneTimeout.promise]);
    } finally {
      doneTimeout.cancel();
    }

    if (sender.finalized) return;
    this.completeSender(sender, digest);
  }

  /* ---------------------------------------------------------------------- */
  /* Receiving                                                              */
  /* ---------------------------------------------------------------------- */

  private handleOffer(peerId: string, payload: Extract<TransferMessage, { type: 'transfer_offer' }>['payload']): void {
    const { transferId, file } = payload;
    const transport = this.transports.get(peerId);

    if (this.senders.has(transferId) || this.receivers.has(transferId) || this.pendingOffers.has(transferId)) {
      transport?.sendControl(
        createTransferMessage('transfer_reject', {
          transferId,
          reason: 'Duplicate transfer id.',
          code: 'VALIDATION_FAILED',
        }),
      );
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      transport?.sendControl(
        createTransferMessage('transfer_reject', {
          transferId,
          reason: 'File exceeds the size limit.',
          code: 'PAYLOAD_TOO_LARGE',
        }),
      );
      return;
    }

    if (!transport || !transport.isOpen()) return;

    const autoAccept = this.options.getSettings().autoAcceptTransfers;
    const now = this.now();
    const chunkSize = clampChunkSize(payload.chunkSize);
    const record = this.makeRecord({
      id: transferId,
      direction: 'receive',
      peerId,
      peerName: transport.peerName,
      file,
      chunkSize,
      status: autoAccept ? 'connecting' : 'waiting',
      requiresAcceptance: !autoAccept,
      canRetry: false,
      now,
    });

    this.options.sink.upsert(record);

    const pending: PendingOffer = {
      id: transferId,
      peerId,
      peerName: transport.peerName,
      file,
      chunkSize,
      record,
      transport,
    };

    if (autoAccept) {
      void this.beginReceive(pending);
    } else {
      this.pendingOffers.set(transferId, pending);
      this.pendingTimers.set(
        transferId,
        setTimeout(() => {
          const stillPending = this.pendingOffers.get(transferId);
          if (!stillPending) return;
          this.pendingOffers.delete(transferId);
          this.pendingTimers.delete(transferId);
          stillPending.transport.sendControl(
            createTransferMessage('transfer_reject', {
              transferId,
              reason: 'The recipient did not respond in time.',
              code: 'TRANSFER_REJECTED',
            }),
          );
          this.finalizePending(stillPending, 'The request expired.');
        }, 120_000),
      );
      this.options.onIncomingOffer?.(record);
    }
  }

  private async beginReceive(pending: PendingOffer): Promise<void> {
    this.clearPendingTimer(pending.id);
    let writer: ReceiveWriter;
    try {
      writer = await createReceiveWriter(sanitizeFileName(pending.file.name), pending.file.size);
    } catch {
      this.rejectOffer(pending, 'STORAGE_ERROR', 'The receiving device could not create the file.');
      return;
    }

    if (this.disposed) {
      await writer.abort();
      return;
    }

    const now = this.now();
    const receiver: ReceiverState = {
      id: pending.id,
      peerId: pending.peerId,
      peerName: pending.peerName,
      file: pending.file,
      chunkSize: pending.chunkSize,
      writer,
      digest: new Sha256(),
      bytesReceived: 0,
      expectedIndex: 0,
      writeChain: Promise.resolve(),
      writeError: null,
      record: pending.record,
      speed: 0,
      sampleAt: now,
      sampleBytes: 0,
      lastReportAt: 0,
      lastActivityAt: now,
      completed: false,
      finalized: false,
    };

    this.receivers.set(receiver.id, receiver);
    this.pendingOffers.delete(receiver.id);

    const accept = createTransferMessage('transfer_accept', {
      transferId: receiver.id,
      chunkSize: receiver.chunkSize,
    });

    if (!pending.transport.sendControl(accept)) {
      void this.failReceiver(receiver, 'TRANSFER_FAILED', 'The peer connection closed.', false);
      return;
    }

    this.patchReceiver(receiver, {
      status: 'transferring',
      requiresAcceptance: false,
      storageKey: writer.key,
      startedAt: now,
    });
  }

  private handleChunk(
    peerId: string,
    frame: { transferId: string; chunkIndex: number; payload: Uint8Array },
  ): void {
    const receiver = this.receivers.get(frame.transferId);
    if (!receiver || receiver.finalized || receiver.peerId !== peerId) return;

    if (frame.chunkIndex !== receiver.expectedIndex) {
      void this.failReceiver(
        receiver,
        'TRANSFER_FAILED',
        'Chunks arrived out of order.',
        true,
      );
      return;
    }

    if (receiver.bytesReceived + frame.payload.byteLength > receiver.file.size) {
      void this.failReceiver(
        receiver,
        'TRANSFER_FAILED',
        'The sender exceeded the declared file size.',
        true,
      );
      return;
    }

    receiver.digest.update(frame.payload);
    receiver.bytesReceived += frame.payload.byteLength;
    receiver.expectedIndex += 1;
    receiver.lastActivityAt = this.now();

    // Keep arrival order without blocking the data channel handler.
    receiver.writeChain = receiver.writeChain.then(async () => {
      await receiver.writer.write(frame.payload);
    });
    receiver.writeChain.catch((error: unknown) => {
      receiver.writeError = error;
    });

    this.reportReceiverProgress(receiver);
  }

  private async handleFileComplete(
    peerId: string,
    payload: Extract<TransferMessage, { type: 'file_complete' }>['payload'],
  ): Promise<void> {
    const receiver = this.receivers.get(payload.transferId);
    if (!receiver || receiver.finalized || receiver.peerId !== peerId) return;
    if (receiver.completed) return;
    receiver.completed = true;

    try {
      await receiver.writeChain;

      if (receiver.writeError) {
        throw new TransferAbort('STORAGE_ERROR', 'Could not write the file to local storage.');
      }
      if (receiver.bytesReceived !== payload.size || receiver.expectedIndex !== payload.chunks) {
        throw new TransferAbort('TRANSFER_FAILED', 'The received file is incomplete.');
      }

      const digest = receiver.digest.hex();
      if (digest !== payload.sha256) {
        receiver.finalized = true;
        this.receivers.delete(receiver.id);
        await receiver.writer.abort();
        this.patchReceiver(receiver, {
          status: 'failed',
          integrity: 'mismatch',
          sha256: digest,
          error: 'Integrity check failed — the file was discarded.',
          completedAt: this.now(),
        });
        this.notifyPeerError(receiver, 'HASH_MISMATCH', 'The received file failed its integrity check.');
        this.finishReceiver(receiver);
        return;
      }

      await receiver.writer.close();
      receiver.finalized = true;
      this.receivers.delete(receiver.id);

      this.patchReceiver(receiver, {
        status: 'completed',
        integrity: 'verified',
        sha256: digest,
        bytesTransferred: payload.size,
        chunksTransferred: payload.chunks,
        speedBps: 0,
        error: null,
        completedAt: this.now(),
        canRetry: false,
      });

      this.finishReceiver(receiver);

      const transport = this.transports.get(peerId);
      transport?.sendControl(createTransferMessage('transfer_done', { transferId: receiver.id }));

      if (this.options.getSettings().autoDownload) {
        this.options.onAutoDownload?.(receiver.record);
      }
    } catch (error) {
      if (error instanceof TransferAbort) {
        await this.failReceiver(receiver, error.code, error.message, error.notifyPeer);
      } else {
        await this.failReceiver(receiver, 'STORAGE_ERROR', errorMessage(error), true);
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Public controls                                                        */
  /* ---------------------------------------------------------------------- */

  handleControl(peerId: string, message: TransferMessage): void {
    if (this.disposed) return;

    switch (message.type) {
      case 'transfer_offer':
        this.handleOffer(peerId, message.payload);
        break;

      case 'transfer_accept': {
        const sender = this.senders.get(message.payload.transferId);
        if (!sender || sender.peerId !== peerId) return;
        sender.accept.resolve(message.payload.chunkSize ?? sender.chunkSize);
        break;
      }

      case 'transfer_reject': {
        const sender = this.senders.get(message.payload.transferId);
        if (!sender || sender.peerId !== peerId) return;
        this.failSender(
          sender,
          message.payload.code ?? 'TRANSFER_REJECTED',
          message.payload.reason ?? 'The other device declined the transfer.',
          false,
        );
        break;
      }

      case 'file_complete':
        void this.handleFileComplete(peerId, message.payload);
        break;

      case 'transfer_done': {
        const sender = this.senders.get(message.payload.transferId);
        if (!sender || sender.peerId !== peerId) return;
        sender.done.resolve();
        break;
      }

      case 'transfer_error': {
        const sender = this.senders.get(message.payload.transferId);
        if (sender && sender.peerId === peerId) {
          this.failSender(
            sender,
            message.payload.code,
            message.payload.message ?? 'The receiving device reported an error.',
            false,
          );
        }
        const receiver = this.receivers.get(message.payload.transferId);
        if (receiver && receiver.peerId === peerId) {
          void this.failReceiver(
            receiver,
            message.payload.code,
            message.payload.message ?? 'The sending device reported an error.',
            false,
          );
        }
        const pending = this.pendingOffers.get(message.payload.transferId);
        if (pending && pending.peerId === peerId) {
          this.pendingOffers.delete(pending.id);
          this.clearPendingTimer(pending.id);
          this.finalizePending(
            pending,
            message.payload.message ?? 'The sender cancelled the request.',
          );
        }
        break;
      }

      case 'transfer_cancel': {
        const sender = this.senders.get(message.payload.transferId);
        if (sender && sender.peerId === peerId && !sender.finalized) {
          this.finalizeSender(sender, 'cancelled', 'The other device cancelled the transfer.');
        }
        const receiver = this.receivers.get(message.payload.transferId);
        if (receiver && receiver.peerId === peerId && !receiver.finalized) {
          void this.finalizeReceiver(receiver, 'cancelled', 'The sender cancelled the transfer.');
        }
        const pending = this.pendingOffers.get(message.payload.transferId);
        if (pending && pending.peerId === peerId) {
          this.pendingOffers.delete(pending.id);
          this.clearPendingTimer(pending.id);
          this.finalizePending(pending, 'The sender cancelled the transfer.');
        }
        break;
      }
    }
  }

  onChunk(peerId: string, frame: { transferId: string; chunkIndex: number; payload: Uint8Array }): void {
    this.handleChunk(peerId, frame);
  }

  acceptIncoming(id: string): void {
    const pending = this.pendingOffers.get(id);
    if (!pending) return;
    this.pendingOffers.delete(id);
    this.clearPendingTimer(id);
    void this.beginReceive(pending);
  }

  declineIncoming(id: string): void {
    const pending = this.pendingOffers.get(id);
    if (!pending) return;
    this.pendingOffers.delete(id);
    this.clearPendingTimer(id);
    pending.transport.sendControl(
      createTransferMessage('transfer_reject', { transferId: id, reason: 'Declined by the recipient.' }),
    );
    this.finalizePending(pending, 'Declined.');
  }

  cancel(id: string): void {
    const sender = this.senders.get(id);
    if (sender && !sender.finalized) {
      const transport = this.transports.get(sender.peerId);
      transport?.sendControl(
        createTransferMessage('transfer_cancel', { transferId: id, by: 'sender', reason: 'Cancelled by sender.' }),
      );
      this.finalizeSender(sender, 'cancelled', 'You cancelled this transfer.');
      return;
    }

    const receiver = this.receivers.get(id);
    if (receiver && !receiver.finalized) {
      const transport = this.transports.get(receiver.peerId);
      transport?.sendControl(
        createTransferMessage('transfer_cancel', { transferId: id, by: 'receiver', reason: 'Cancelled by recipient.' }),
      );
      void this.finalizeReceiver(receiver, 'cancelled', 'You cancelled this transfer.');
      return;
    }

    const pending = this.pendingOffers.get(id);
    if (pending) {
      this.pendingOffers.delete(id);
      this.clearPendingTimer(id);
      pending.transport.sendControl(
        createTransferMessage('transfer_reject', { transferId: id, reason: 'Declined by the recipient.' }),
      );
      this.finalizePending(pending, 'Declined.');
    }
  }

  retry(id: string): boolean {
    const entry = this.retryEntries.get(id);
    if (!entry) return false;
    this.retryEntries.delete(id);
    if (this.transports.get(entry.peerId)?.isOpen() !== true) {
      this.options.sink.notify('That device is no longer connected.', 'error');
      return false;
    }
    this.options.sink.remove(id);
    return this.enqueue([entry.file], [{ id: entry.peerId, name: entry.peerName }]) > 0;
  }

  /* ---------------------------------------------------------------------- */
  /* Finalisation                                                           */
  /* ---------------------------------------------------------------------- */

  private completeSender(sender: SenderState, digest: string): void {
    if (sender.finalized) return;
    sender.finalized = true;
    this.senders.delete(sender.id);
    this.retryEntries.delete(sender.id);
    this.patchSender(sender, {
      status: 'completed',
      bytesTransferred: sender.file.size,
      chunksTransferred: sender.chunksSent,
      totalChunks: totalChunksFor(sender.file.size, sender.chunkSize),
      speedBps: 0,
      integrity: 'verified',
      sha256: digest,
      error: null,
      completedAt: this.now(),
      canRetry: false,
    });
    this.finishSender(sender);
  }

  private failSender(
    sender: SenderState,
    code: ErrorCode,
    message: string,
    notifyPeer: boolean,
  ): void {
    if (sender.finalized) return;
    sender.finalized = true;
    this.senders.delete(sender.id);
    this.queue.splice(
      0,
      this.queue.length,
      ...this.queue.filter((id) => id !== sender.id),
    );

    if (notifyPeer) {
      this.transports
        .get(sender.peerId)
        ?.sendControl(
          createTransferMessage('transfer_error', {
            transferId: sender.id,
            code,
            message: message.slice(0, 240),
          }),
        );
    }

    // A failure is retryable, a user cancellation is not.
    this.patchSender(sender, {
      status: 'failed',
      speedBps: 0,
      error: message,
      completedAt: this.now(),
      canRetry: true,
    });
    this.finishSender(sender);
  }

  private finalizeSender(sender: SenderState, status: 'cancelled', message: string): void {
    if (sender.finalized) return;
    sender.finalized = true;
    sender.cancelled = true;
    this.senders.delete(sender.id);
    this.queue.splice(
      0,
      this.queue.length,
      ...this.queue.filter((id) => id !== sender.id),
    );
    this.retryEntries.delete(sender.id);
    sender.accept.reject(new CancelledByUser());
    sender.done.reject(new CancelledByUser());

    this.patchSender(sender, {
      status,
      speedBps: 0,
      error: message,
      completedAt: this.now(),
      canRetry: false,
    });
    this.finishSender(sender);
  }

  private async failReceiver(
    receiver: ReceiverState,
    code: ErrorCode,
    message: string,
    notifyPeer: boolean,
  ): Promise<void> {
    if (receiver.finalized) return;
    receiver.finalized = true;
    this.receivers.delete(receiver.id);
    await receiver.writer.abort().catch(() => undefined);

    if (notifyPeer) this.notifyPeerError(receiver, code, message);

    this.patchReceiver(receiver, {
      status: 'failed',
      speedBps: 0,
      error: message,
      storageKey: null,
      completedAt: this.now(),
      canRetry: false,
    });
    this.finishReceiver(receiver);
  }

  private async finalizeReceiver(
    receiver: ReceiverState,
    status: 'cancelled',
    message: string,
  ): Promise<void> {
    if (receiver.finalized) return;
    receiver.finalized = true;
    this.receivers.delete(receiver.id);
    await receiver.writer.abort().catch(() => undefined);

    this.patchReceiver(receiver, {
      status,
      speedBps: 0,
      error: message,
      storageKey: null,
      completedAt: this.now(),
      canRetry: false,
    });
    this.finishReceiver(receiver);
  }

  private finalizePending(pending: PendingOffer, message: string): void {
    this.clearPendingTimer(pending.id);
    this.patchPending(pending, {
      status: 'cancelled',
      error: message,
      completedAt: this.now(),
      requiresAcceptance: false,
      canRetry: false,
    });
    this.retryEntries.delete(pending.id);
  }

  private clearPendingTimer(id: string): void {
    const timer = this.pendingTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.pendingTimers.delete(id);
    }
  }

  private rejectOffer(pending: PendingOffer, code: ErrorCode, reason: string): void {
    pending.transport.sendControl(
      createTransferMessage('transfer_reject', { transferId: pending.id, reason, code }),
    );
    this.patchPending(pending, {
      status: 'failed',
      error: reason,
      completedAt: this.now(),
      requiresAcceptance: false,
      canRetry: false,
    });
  }

  private notifyPeerError(receiver: ReceiverState, code: ErrorCode, message: string): void {
    this.transports.get(receiver.peerId)?.sendControl(
      createTransferMessage('transfer_error', {
        transferId: receiver.id,
        code,
        message: message.slice(0, 240),
      }),
    );
  }

  private finishSender(sender: SenderState): void {
    this.options.sink.patch(sender.id, sender.record);
    this.options.sink.finish(sender.record);
  }

  private finishReceiver(receiver: ReceiverState): void {
    this.options.sink.patch(receiver.id, receiver.record);
    this.options.sink.finish(receiver.record);
  }

  /* ---------------------------------------------------------------------- */
  /* Progress                                                               */
  /* ---------------------------------------------------------------------- */

  private reportSenderProgress(sender: SenderState, force = false): void {
    const now = this.now();
    if (!force && now - sender.lastReportAt < 100) return;
    sender.lastReportAt = now;
    sender.speed = this.sampleSpeed(sender, sender.bytesSent);

    this.patchSender(sender, {
      bytesTransferred: sender.bytesSent,
      chunksTransferred: sender.chunksSent,
      speedBps: sender.speed,
    });
  }

  private reportReceiverProgress(receiver: ReceiverState, force = false): void {
    const now = this.now();
    if (!force && now - receiver.lastReportAt < 100) return;
    receiver.lastReportAt = now;
    receiver.speed = this.sampleSpeed(receiver, receiver.bytesReceived);

    this.patchReceiver(receiver, {
      bytesTransferred: receiver.bytesReceived,
      chunksTransferred: receiver.expectedIndex,
      speedBps: receiver.speed,
    });
  }

  private sampleSpeed(
    state: { speed: number; sampleAt: number; sampleBytes: number },
    bytes: number,
  ): number {
    const now = this.now();
    const elapsed = now - state.sampleAt;
    if (elapsed < 200) return state.speed;

    const instant = ((bytes - state.sampleBytes) / elapsed) * 1000;
    state.speed = state.speed > 0 ? state.speed * 0.7 + instant * 0.3 : instant;
    state.sampleAt = now;
    state.sampleBytes = bytes;
    return state.speed;
  }

  private patchSender(sender: SenderState, patch: Partial<TransferRecord>): void {
    sender.record = { ...sender.record, ...patch, updatedAt: this.now() };
    this.options.sink.patch(sender.id, patch);
  }

  private patchReceiver(receiver: ReceiverState, patch: Partial<TransferRecord>): void {
    receiver.record = { ...receiver.record, ...patch, updatedAt: this.now() };
    this.options.sink.patch(receiver.id, patch);
  }

  private patchPending(pending: PendingOffer, patch: Partial<TransferRecord>): void {
    pending.record = { ...pending.record, ...patch, updatedAt: this.now() };
    this.options.sink.patch(pending.id, patch);
  }

  /* ---------------------------------------------------------------------- */
  /* Helpers                                                                */
  /* ---------------------------------------------------------------------- */

  private sweepIdleReceivers(): void {
    const now = this.now();
    for (const receiver of [...this.receivers.values()]) {
      if (receiver.finalized) continue;
      if (now - receiver.lastActivityAt > IDLE_RECEIVE_TIMEOUT_MS) {
        void this.failReceiver(
          receiver,
          'TRANSFER_FAILED',
          'The sender stopped responding.',
          true,
        );
      }
    }
  }

  private makeRecord(params: {
    id: string;
    direction: 'send' | 'receive';
    peerId: string;
    peerName: string;
    file: FileMeta;
    chunkSize: number;
    status: TransferStatus;
    requiresAcceptance: boolean;
    canRetry: boolean;
    now: number;
  }): TransferRecord {
    return {
      id: params.id,
      direction: params.direction,
      peerId: params.peerId,
      peerName: params.peerName,
      fileName: params.file.name,
      fileSize: params.file.size,
      mimeType: params.file.mimeType ?? null,
      status: params.status,
      bytesTransferred: 0,
      chunksTransferred: 0,
      totalChunks: totalChunksFor(params.file.size, params.chunkSize),
      chunkSize: params.chunkSize,
      startedAt: params.now,
      updatedAt: params.now,
      completedAt: null,
      speedBps: 0,
      error: null,
      integrity: 'pending',
      sha256: null,
      storageKey: null,
      requiresAcceptance: params.requiresAcceptance,
      canRetry: params.canRetry,
    };
  }
}
